import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Table as TableIcon, Calendar } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden"
  },
  headerBox: {
    padding: theme.spacing(2, 2.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.text.primary
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: "0.825rem",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap"
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  tableCell: {
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap"
  },
  costChip: {
    fontWeight: 600,
    fontSize: "0.775rem",
    backgroundColor: "rgba(16, 185, 129, 0.12)",
    color: "#059669"
  },
  emptyNotice: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary
  }
}));

const formatCurrency = (val, currency = "USD", decimals = 2) => {
  const num = Number(val || 0);
  const fractionDigits = num > 0 && num < 0.01 ? 4 : decimals;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits
    }).format(num);
  } catch (e) {
    return `${currency} ${num.toFixed(fractionDigits)}`;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const formatNumber = (val) => {
  return new Intl.NumberFormat("pt-BR").format(Number(val || 0));
};

const DailyTable = ({ dailyHistory = [], currency = "USD" }) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Ordena os dias decrescente para exibir os mais recentes no topo
  const reversedHistory = [...dailyHistory].reverse();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedDays = reversedHistory.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper className={classes.paper} elevation={0}>
      <Box className={classes.headerBox}>
        <TableIcon size={20} />
        <Typography className={classes.title}>
          Detalhamento Diário de Consumo e Custos
        </Typography>
      </Box>

      {reversedHistory.length === 0 ? (
        <Typography className={classes.emptyNotice}>
          Nenhum registro diário encontrado para o período selecionado.
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>Data</TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    Total Mensagens
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    Marketing
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    Serviço
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    Utilidade
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    Autenticação
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    Gasto do Dia
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDays.map((row) => (
                  <TableRow key={row.date} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      <Box display="flex" alignItems="center" gridGap={6}>
                        <Calendar size={14} color="#6b7280" />
                        <span>{formatDate(row.date)}</span>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      <strong>{formatNumber(row.totalVolume)}</strong>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      {formatNumber(row.categories?.MARKETING?.volume || 0)}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      {formatNumber(row.categories?.SERVICE?.volume || 0)}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      {formatNumber(row.categories?.UTILITY?.volume || 0)}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      {formatNumber(row.categories?.AUTHENTICATION?.volume || 0)}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      <Chip
                        size="small"
                        label={formatCurrency(row.totalCost, currency)}
                        className={classes.costChip}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={reversedHistory.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </>
      )}
    </Paper>
  );
};

export default DailyTable;
